import { CloudCustomerAddress } from "@omni/types/api/customer/cloud/address.model";
import { AddressType } from "@omni/types/api/customer/cloud/shared/enums/addressType.enum";
import { Country } from "@omni/types/enums/country.enum";
import { Button } from "@omni/ui/components/Button/button.component";
import { cn } from "@omni/utils/styles";
import { useState } from "react";

import { CustomerAddress } from "@/components/Address/Address/address.component";
import { useAddressValidationContext } from "@/components/Address/AddressValidation/context/addressValidation.context";
import { Col } from "@/components/General/Grid/col.component";
import { Container } from "@/components/General/Grid/container.component";
import { Row } from "@/components/General/Grid/row.component";
import { DeleteAddressModal } from "@/components/Modals/DeleteAddressModal/deleteAddressModal.container";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";

type AddressBlockProps = {
    address: CloudCustomerAddress;
    updateDefaultBilling: (address: CloudCustomerAddress) => Promise<void>;
    updateDefaultDelivery: (address: CloudCustomerAddress) => Promise<void>;
    canDelete: boolean;
    addressType?: AddressType;
};

const AddressBlock = ({
    address,
    updateDefaultBilling,
    updateDefaultDelivery,
    canDelete,
    addressType
}: AddressBlockProps) => {
    const { t } = useGranite();
    const [isOpenDeleteAddressModal, setIsOpenDeleteAddressModal] =
        useState(false);

    const { openAddressModal } = useAddressValidationContext();

    const handleOnOpenEditAddressModal = () => {
        openAddressModal(address);
    };

    return (
        <>
            <DeleteAddressModal
                addressId={address.id}
                isOpen={isOpenDeleteAddressModal}
                onClose={() => setIsOpenDeleteAddressModal(false)}
            />
            <Container
                className={cn(
                    "tw:p-2 tw:border tw:border-solid tw:border-divider"
                )}
            >
                <Row className={cn("tw:mb-2")}>
                    <Col>
                        <CustomerAddress
                            address={address}
                            addressType={addressType}
                        />
                    </Col>
                    <Col
                        xs="auto"
                        className={cn("tw:text-right tw:grow-0!")}
                    >
                        {canDelete && (
                            <>
                                <Button
                                    test="addressBlock-button-delete"
                                    color="link"
                                    type="button"
                                    className={"p-0"}
                                    onClick={() => {
                                        setIsOpenDeleteAddressModal(true);
                                    }}
                                >
                                    <i className="las la-trash mr-2" />
                                    <span className="d-none d-sm-inline-block">
                                        {t("address.action.delete")}
                                    </span>
                                </Button>
                                <br />
                            </>
                        )}
                        <Button
                            test="addressBlock-button-edit"
                            color="link"
                            type="button"
                            className={"p-0"}
                            onClick={handleOnOpenEditAddressModal}
                        >
                            <i className="las la-pencil-alt mr-2" />
                            <span className="d-none d-sm-inline-block">
                                {t("address.action.edit")}
                            </span>
                        </Button>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        {!address.defaultDeliveryAddress && (
                            <>
                                <Button
                                    test="addressBlock-button-updateDefaultDelivery"
                                    color="link"
                                    type="button"
                                    className={"p-0"}
                                    onClick={() => {
                                        updateDefaultDelivery(address);
                                    }}
                                    disabled={address.country !== Country.Be}
                                >
                                    <i className="las la-home" />
                                    <span className="pl-2">
                                        {t(
                                            address.country === Country.Be
                                                ? "address.action.makeDefaultDelivery"
                                                : "address.action.onlyBEDelivery"
                                        )}
                                    </span>
                                </Button>
                                <br />
                            </>
                        )}
                        {!address.defaultPaymentAddress && (
                            <Button
                                test="addressBlock-button-makeDefaultPayment"
                                color="link"
                                type="button"
                                className={"p-0"}
                                onClick={() => {
                                    updateDefaultBilling(address);
                                }}
                            >
                                <i className="las la-file-invoice" />
                                <span className="pl-2">
                                    {t("address.action.makeDefaultPayment")}
                                </span>
                            </Button>
                        )}
                    </Col>
                </Row>
            </Container>
        </>
    );
};

export { AddressBlock };
